// ============================================================
//  src/admin/pages/AdminContactSaverPage.js — Bot-Father → Contact Saver
//  Prices (first number / each extra number), how many numbers a user may link,
//  plus who is paying, how much it earns and whether each number is checking in.
// ============================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getContactSaverAdmin, saveContactSaverAdmin } from '../api/adminClient';
import { PageHeader, StatCard, Badge } from '../components/UI';
import DataTable from '../components/DataTable';

const naira = n => `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const day = s => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const ago = secs => (secs == null ? 'never' : secs < 60 ? 'just now' : secs < 3600 ? `${Math.floor(secs / 60)} min ago` : secs < 86400 ? `${Math.round(secs / 3600)} h ago` : `${Math.round(secs / 86400)} d ago`);

export default function AdminContactSaverPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ first: '', extra: '', maxNumbers: '', contest: '' });
  const [saving, setSaving] = useState(false);

  const load = () => getContactSaverAdmin().then(res => {
    setData(res.data);
    const p = res.data.prices || {};
    setForm({ first: String(p.first ?? ''), extra: String(p.extra ?? ''), maxNumbers: String(p.maxNumbers ?? ''), contest: String(p.contest ?? '') });
  }).catch(e => { toast.error(e?.response?.data?.message || 'Could not load'); setData({ stats: {}, list: [], prices: {} }); });
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await saveContactSaverAdmin({ first: Number(form.first), extra: Number(form.extra), maxNumbers: Number(form.maxNumbers), contest: form.contest === '' ? '' : Number(form.contest) });
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Save failed'); }
    setSaving(false);
  }

  if (!data) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading…</div>;
  const s = data.stats || {};
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const status = r => {
    if (r.status !== 'linked') return <Badge tone="muted">{r.status === 'logged_out' ? 'Unlinked' : 'Not linked'}</Badge>;
    if (!r.paid) return <Badge tone="warning">Not paid</Badge>;
    if (!r.on) return <Badge tone="muted">Paused</Badge>;
    if (r.lastCheckAgo != null && r.lastCheckAgo > 15 * 60) return <Badge tone="warning">Late</Badge>;
    return <Badge tone="success">Running</Badge>;
  };
  const columns = [
    { key: 'label', label: 'Number', primary: true, render: r => <span><b>{r.label}</b><br /><small style={{ color: 'var(--bf-muted, #6b7280)' }}>{r.phone || 'not linked'}</small></span> },
    { key: 'owner', label: 'Owner', render: r => <span>{r.owner || '—'}<br /><small style={{ color: 'var(--bf-muted, #6b7280)' }}>{r.email}</small></span> },
    { key: 'status', label: 'Status', render: r => <span title={r.error || ''}>{status(r)}</span> },
    { key: 'check', label: 'Last check', render: r => ago(r.lastCheckAgo) },
    { key: 'saved', label: 'Saved', render: r => Number(r.saved).toLocaleString() },
    { key: 'until', label: 'Paid until', render: r => day(r.until) },
    { key: 'autoRenew', label: 'Auto-renew', render: r => (r.autoRenew ? 'On' : 'Off') },
  ];

  return (
    <div className="bf-page">
      <PageHeader title="Contact Saver"
        subtitle="Users link their WhatsApp; anyone who messages them and isn’t saved is added to their phone’s contacts. Paid monthly per number from the CommandKit wallet." />

      <div className="bf-stat-grid">
        <StatCard label="Paying numbers" value={s.paying ?? 0} sub={`${s.autoRenew ?? 0} on auto-renew · ${s.users ?? 0} users`} accent="var(--bf-success)" />
        <StatCard label="Running now" value={s.running ?? 0} sub={`${s.linked ?? 0} linked`} />
        <StatCard label="Revenue · last 30 days" value={naira(s.revenue30d)} sub={`${naira(s.revenueAll)} all time`} />
        <StatCard label="Contacts saved" value={(s.savedToday ?? 0).toLocaleString()} sub={`today · ${(s.savedTotal ?? 0).toLocaleString()} all time`} />
      </div>

      <form onSubmit={save} className="bf-card" style={{ maxWidth: 640 }}>
        <div className="bf-card-hdr">Pricing</div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="bf-field">
            <label className="bf-field-label" htmlFor="cs-first">First number — ₦ per month</label>
            <input id="cs-first" className="bf-input" type="number" min="100" step="50" value={form.first} onChange={set('first')} required />
          </div>
          <div className="bf-field">
            <label className="bf-field-label" htmlFor="cs-extra">Each extra number — ₦ per month</label>
            <input id="cs-extra" className="bf-input" type="number" min="100" step="50" value={form.extra} onChange={set('extra')} required />
            <div className="bf-field-hint">A user’s oldest number pays the first price; every other number they add pays this.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label" htmlFor="cs-max">Numbers each user can link</label>
            <input id="cs-max" className="bf-input" type="number" min="1" max="50" step="1" value={form.maxNumbers} onChange={set('maxNumbers')} required />
            <div className="bf-field-hint">Each number adds a few seconds to the 5-minute check on your server — keep this modest on shared hosting.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label" htmlFor="cs-contest">Referral Contest add-on — ₦ per number per month</label>
            <input id="cs-contest" className="bf-input" type="number" min="100" step="50" value={form.contest} onChange={set('contest')} required />
            <div className="bf-field-hint">Charged separately from Contact Saver. A number using both pays both.</div>
          </div>
          <div className="bf-field-hint">New prices apply to new payments and the next auto-renewals. Months already paid aren’t changed.</div>
          <button className="btn btn-primary" type="submit" disabled={saving} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>

      <div className="bf-card">
        <div className="bf-card-hdr">Numbers</div>
        <DataTable columns={columns} rows={data.list || []} keyField="id"
          onRowClick={r => r.ownerId && navigate(`/bot-father/users/${r.ownerId}`)}
          emptyMessage="No one has linked a number yet." />
      </div>
    </div>
  );
}